import React, { useState, useEffect } from "react";
import { Card, Select, DatePicker, Button, Form, message, Alert, Spin } from "antd";
import { CalendarOutlined, DatabaseOutlined, DownloadOutlined } from "@ant-design/icons";
import { authAxios } from "../utils/session_utils";
import dayjs, { Dayjs } from "dayjs";

const { Option } = Select;

interface Program {
  id: string;
  name: string;
  value: string;
}

interface AvailabilitySchedulesFormData {
  program: string;
  date: Dayjs;
}

const AvailabilitySchedules: React.FC = () => {
  const [form] = Form.useForm();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Fetch programs on component mount
  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    setLoadingPrograms(true);
    try {
      const response = await authAxios({
        method: "GET",
        url: `${process.env.REACT_APP_BACK_END_API}/salesforce/programs`,
      });

      if (response.data.success) {
        setPrograms(response.data.programs);
      } else {
        message.error("Failed to fetch programs");
      }
    } catch (error: any) {
      console.error("Error fetching programs:", error);
      message.error(error.response?.data?.error || "Failed to fetch programs");
    } finally {
      setLoadingPrograms(false);
    }
  };

  const onFinish = async (values: AvailabilitySchedulesFormData) => {
    const { program, date } = values;
    
    if (!program || !date) {
      message.error("Please select both program and date");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authAxios({
        method: "POST",
        url: `${process.env.REACT_APP_BACK_END_API}/salesforce/availability-schedules`,
        data: {
          program: program, // This will now be the program ID
          date: date.format("YYYY-MM-DD"),
        },
      });

      if (response.data.success) {
        message.success(response.data.message);
        form.resetFields();
      } else {
        message.error(response.data.error || "Failed to create availability schedule");
      }
    } catch (error: any) {
      console.error("Error creating availability schedule:", error);
      message.error(error.response?.data?.error || "Failed to create availability schedule");
    } finally {
      setSubmitting(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error("Form validation failed:", errorInfo);
  };

  return (
    <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DatabaseOutlined />
            <span>Salesforce Availability Schedules</span>
          </div>
        }
        bordered
      >
        <div style={{ marginBottom: "24px" }}>
          <Alert
            message="Create Availability Schedules"
            description="Select a program and date to generate availability schedules in Salesforce."
            type="info"
            showIcon
            style={{ marginBottom: "24px" }}
          />
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item
            label="Program"
            name="program"
            rules={[{ required: true, message: "Please select a program" }]}
          >
            <Select
              placeholder="Select a program"
              loading={loadingPrograms}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
            >
              {programs.map((program) => (
                <Option key={program.value} value={program.value}>
                  {program.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Select date"
              suffixIcon={<CalendarOutlined />}
              disabledDate={(current) => {
                // Optionally disable past dates
                return current && current < dayjs().startOf("day");
              }}
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", gap: "12px" }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<DownloadOutlined />}
                size="large"
              >
                {submitting ? "Creating Schedule..." : "Create Availability Schedule"}
              </Button>
              
              <Button
                onClick={() => form.resetFields()}
                disabled={submitting}
                size="large"
              >
                Clear Form
              </Button>
            </div>
          </Form.Item>
        </Form>

        {loadingPrograms && programs.length === 0 && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin />
            <div style={{ marginTop: "10px" }}>Loading programs...</div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AvailabilitySchedules;